/**
 * 文本相似度算法库
 * 用于画板名称匹配和推荐
 */

/**
 * 通用分词函数
 */
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, " ")
    .split(/\s+/)
    .filter((token) => token.length > 0);
}

/**
 * 通用分词函数（返回Set）
 */
function tokenizeToSet(text: string): Set<string> {
  return new Set(tokenize(text));
}

/**
 * TF-IDF + 余弦相似度算法
 * 权重：20%
 */
export function calculateTfIdfCosineSimilarity(
  text1: string,
  text2: string
): number {
  if (!text1 || !text2) return 0;

  // 使用通用分词函数

  const tokens1 = tokenize(text1);
  const tokens2 = tokenize(text2);

  if (tokens1.length === 0 || tokens2.length === 0) return 0;

  // 计算词频
  const getTermFreq = (tokens: string[]): Map<string, number> => {
    const tf = new Map<string, number>();
    tokens.forEach((token) => {
      tf.set(token, (tf.get(token) || 0) + 1);
    });
    return tf;
  };

  const tf1 = getTermFreq(tokens1);
  const tf2 = getTermFreq(tokens2);

  // 计算所有词汇
  const allTerms = new Set([...tf1.keys(), ...tf2.keys()]);

  // 计算TF-IDF向量
  const getTfIdfVector = (
    tf: Map<string, number>,
    allTerms: Set<string>
  ): number[] => {
    const vector: number[] = [];
    allTerms.forEach((term) => {
      const termFreq = tf.get(term) || 0;
      // 改进IDF计算，避免过度惩罚
      const idf = Math.log((allTerms.size + 2) / (termFreq + 1));
      vector.push(termFreq * idf);
    });
    return vector;
  };

  const vector1 = getTfIdfVector(tf1, allTerms);
  const vector2 = getTfIdfVector(tf2, allTerms);

  // 计算余弦相似度
  let dotProduct = 0;
  let norm1 = 0;
  let norm2 = 0;

  for (let i = 0; i < vector1.length; i++) {
    dotProduct += vector1[i] * vector2[i];
    norm1 += vector1[i] * vector1[i];
    norm2 += vector2[i] * vector2[i];
  }

  if (norm1 === 0 || norm2 === 0) return 0;

  return dotProduct / (Math.sqrt(norm1) * Math.sqrt(norm2));
}

/**
 * Jaccard相似度算法
 * 权重：20%
 */
export function calculateJaccardSimilarity(
  text1: string,
  text2: string
): number {
  if (!text1 || !text2) return 0;

  // 使用通用分词函数

  const set1 = tokenizeToSet(text1);
  const set2 = tokenizeToSet(text2);

  if (set1.size === 0 && set2.size === 0) return 1;
  if (set1.size === 0 || set2.size === 0) return 0;

  const intersection = new Set([...set1].filter((x) => set2.has(x)));
  const union = new Set([...set1, ...set2]);

  return intersection.size / union.size;
}

/**
 * Levenshtein相似度算法（编辑距离）
 * 权重：15%
 */
export function calculateLevenshteinSimilarity(
  text1: string,
  text2: string
): number {
  if (!text1 || !text2) return 0;
  if (text1 === text2) return 1;

  const len1 = text1.length;
  const len2 = text2.length;

  if (len1 === 0) return len2 === 0 ? 1 : 0;
  if (len2 === 0) return 0;

  // 使用滚动数组优化空间复杂度
  const maxLen = Math.max(len1, len2);
  let prev = new Array(len2 + 1);
  let curr = new Array(len2 + 1);

  // 初始化
  for (let j = 0; j <= len2; j++) {
    prev[j] = j;
  }

  // 动态规划计算编辑距离
  for (let i = 1; i <= len1; i++) {
    curr[0] = i;
    for (let j = 1; j <= len2; j++) {
      if (text1[i - 1] === text2[j - 1]) {
        curr[j] = prev[j - 1];
      } else {
        curr[j] = Math.min(
          prev[j] + 1, // 删除
          curr[j - 1] + 1, // 插入
          prev[j - 1] + 1 // 替换
        );
      }
    }
    [prev, curr] = [curr, prev];
  }

  const distance = prev[len2];
  return 1 - distance / maxLen;
}

/**
 * N-gram Jaccard相似度算法
 * 权重：25%
 */
export function calculateNgramJaccardSimilarity(
  text1: string,
  text2: string
): number {
  if (!text1 || !text2) return 0;

  const createNgrams = (text: string, n: number): Set<string> => {
    const normalized = text
      .toLowerCase()
      .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, "");
    const ngrams = new Set<string>();

    for (let i = 0; i <= normalized.length - n; i++) {
      ngrams.add(normalized.substring(i, i + n));
    }

    return ngrams;
  };

  // 2-gram 和 3-gram 的组合
  const ngrams1_2 = createNgrams(text1, 2);
  const ngrams2_2 = createNgrams(text2, 2);
  const ngrams1_3 = createNgrams(text1, 3);
  const ngrams2_3 = createNgrams(text2, 3);

  const jaccard2 = calculateJaccardSimilarityFromSets(ngrams1_2, ngrams2_2);
  const jaccard3 = calculateJaccardSimilarityFromSets(ngrams1_3, ngrams2_3);

  // 加权平均
  return jaccard2 * 0.6 + jaccard3 * 0.4;
}

/**
 * 从集合计算Jaccard相似度
 */
function calculateJaccardSimilarityFromSets(
  set1: Set<string>,
  set2: Set<string>
): number {
  if (set1.size === 0 && set2.size === 0) return 1;
  if (set1.size === 0 || set2.size === 0) return 0;

  const intersection = new Set([...set1].filter((x) => set2.has(x)));
  const union = new Set([...set1, ...set2]);

  return intersection.size / union.size;
}

/**
 * Dice系数算法
 * 权重：10%
 */
export function calculateDiceSimilarity(text1: string, text2: string): number {
  if (!text1 || !text2) return 0;

  // 使用通用分词函数

  const set1 = tokenizeToSet(text1);
  const set2 = tokenizeToSet(text2);

  if (set1.size === 0 && set2.size === 0) return 1;
  if (set1.size === 0 || set2.size === 0) return 0;

  const intersection = new Set([...set1].filter((x) => set2.has(x)));

  return (2 * intersection.size) / (set1.size + set2.size);
}

/**
 * 包含度相似度算法
 * 权重：5%
 */
export function calculateContainmentSimilarity(
  text1: string,
  text2: string
): number {
  if (!text1 || !text2) return 0;

  const normalized1 = text1.toLowerCase().trim();
  const normalized2 = text2.toLowerCase().trim();

  if (normalized1 === normalized2) return 1;

  // 字符串级别包含
  const stringContainment =
    normalized1.includes(normalized2) || normalized2.includes(normalized1);
  if (stringContainment) return 0.8;

  // Token级别包含
  const tokens1 = tokenize(normalized1);
  const tokens2 = tokenize(normalized2);

  if (tokens1.length === 0 || tokens2.length === 0) return 0;

  const set1 = new Set(tokens1);
  const set2 = new Set(tokens2);

  const intersection = new Set([...set1].filter((x) => set2.has(x)));
  const minSize = Math.min(set1.size, set2.size);

  return intersection.size / minSize;
}

/**
 * 计算部分匹配相似度（处理包含关系）
 */
export function calculatePartialMatchSimilarity(
  text1: string,
  text2: string
): number {
  if (!text1 || !text2) return 0;

  const normalized1 = text1.toLowerCase().trim();
  const normalized2 = text2.toLowerCase().trim();

  if (normalized1 === normalized2) return 1;

  // 检查包含关系
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
    const shorter =
      normalized1.length < normalized2.length ? normalized1 : normalized2;
    const longer =
      normalized1.length >= normalized2.length ? normalized1 : normalized2;
    return (shorter.length / longer.length) * 0.8; // 包含关系给0.8的权重
  }

  // 检查词汇级别的包含
  const tokens1 = normalized1.split(/\s+/).filter((t) => t.length > 0);
  const tokens2 = normalized2.split(/\s+/).filter((t) => t.length > 0);

  if (tokens1.length === 0 || tokens2.length === 0) return 0;

  const set1 = new Set(tokens1);
  const set2 = new Set(tokens2);

  // 计算词汇包含度
  const intersection = new Set([...set1].filter((x) => set2.has(x)));
  const minSize = Math.min(set1.size, set2.size);

  if (intersection.size === 0) return 0;

  // 如果所有短词汇都在长词汇中，给高分
  const shorterSet = set1.size < set2.size ? set1 : set2;
  const longerSet = set1.size >= set2.size ? set1 : set2;
  const allContained = [...shorterSet].every((token) => longerSet.has(token));

  if (allContained) {
    return 0.7 + (intersection.size / longerSet.size) * 0.3;
  }

  return (intersection.size / minSize) * 0.6;
}

/**
 * 综合相似度计算
 * 使用加权平均组合所有算法
 */
export function calculateOverallSimilarity(
  text1: string,
  text2: string
): number {
  const tfIdfCosine = calculateTfIdfCosineSimilarity(text1, text2) * 0.2;
  const jaccard = calculateJaccardSimilarity(text1, text2) * 0.15;
  const levenshtein = calculateLevenshteinSimilarity(text1, text2) * 0.1;
  const ngramJaccard = calculateNgramJaccardSimilarity(text1, text2) * 0.2;
  const dice = calculateDiceSimilarity(text1, text2) * 0.1;
  const containment = calculateContainmentSimilarity(text1, text2) * 0.05;
  const partialMatch = calculatePartialMatchSimilarity(text1, text2) * 0.2; // 新增部分匹配

  return (
    tfIdfCosine +
    jaccard +
    levenshtein +
    ngramJaccard +
    dice +
    containment +
    partialMatch
  );
}

/**
 * 画板匹配结果接口
 */
export interface BoardMatchResult {
  board: any; // 用户画板对象
  similarity: number; // 相似度分数 (0-1)
  matchedRelatedBoard?: any; // 匹配的同在画板
}

/**
 * 批量匹配画板
 * 返回按相似度排序的匹配结果
 */
export function matchBoards(
  userBoards: any[],
  relatedBoards: any[]
): BoardMatchResult[] {
  if (!userBoards.length || !relatedBoards.length) {
    return [];
  }

  const results: BoardMatchResult[] = [];

  // 为每个用户画板找到最匹配的同在画板
  userBoards.forEach((userBoard) => {
    let bestMatch: BoardMatchResult = {
      board: userBoard,
      similarity: 0.3,
    };

    relatedBoards.forEach((relatedBoard) => {
      const similarity = calculateOverallSimilarity(
        userBoard.title || "",
        relatedBoard.title || ""
      );

      if (similarity > bestMatch.similarity) {
        bestMatch = {
          board: userBoard,
          similarity,
          matchedRelatedBoard: relatedBoard,
        };
      }
    });

    // 总是添加匹配结果，即使相似度为0
    results.push(bestMatch);
  });

  // 按相似度降序排序
  return results.sort((a, b) => b.similarity - a.similarity);
}

/**
 * 获取推荐画板（用于画板数 < 3 的情况）
 * 返回匹配度最高的同在画板
 */
export function getRecommendedRelatedBoards(
  relatedBoards: any[],
  limit: number = 5
): any[] {
  if (!relatedBoards.length) return [];

  // 去重：确保画板名唯一
  const titleMap = new Map();
  const uniqueBoards = relatedBoards.filter((board) => {
    const title = board.title?.toLowerCase().trim();
    if (!title) return false;
    if (titleMap.has(title)) return false;
    titleMap.set(title, true);
    return true;
  });

  // 按标题长度和内容质量排序，优先推荐较短的标题
  return uniqueBoards
    .sort((a, b) => {
      const titleA = (a.title || "").length;
      const titleB = (b.title || "").length;
      return titleA - titleB;
    })
    .slice(0, limit);
}
